import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Pencil, Trash2, Check, X, Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { isHtml, isMarkdown, isJson, isUrl } from "@/lib/textUtils";
import ItemContextMenu from "@/components/shared/ItemContextMenu";
import { DraggableWidgetCard } from "./DraggableWidgetCard";
import { RichContent } from "./RichContent";
import type { DataItem, WidgetItem } from "@/types";
import type { GlobalSelectionContextValue } from "@/hooks/useGlobalSelection";

interface WidgetCardProps {
  widget: WidgetItem;
  data: DataItem;
  widgetHeight: number;
  selectedWidgetIds: Set<string>;
  editingLabel: { dataId: string; value: string } | null;
  setEditingLabel: (v: { dataId: string; value: string } | null) => void;
  handleLabelSave: () => void;
  handleCardClick: (wId: string, dId: string, e: React.MouseEvent) => void;
  handleRightClickSelect: (itemId: string, dataId: string) => void;
  setShowDeleteDialog: (v: boolean) => void;
  globalSelection: GlobalSelectionContextValue;
}

export function WidgetCard({
  widget,
  data,
  widgetHeight,
  selectedWidgetIds,
  editingLabel,
  setEditingLabel,
  handleLabelSave,
  handleCardClick,
  handleRightClickSelect,
  setShowDeleteDialog,
  globalSelection,
}: WidgetCardProps) {
  const { t } = useI18n();
  const [showRaw, setShowRaw] = useState(false);

  const isRich = useMemo(() =>
    data!.mimeType === "text/html" ||
    (data!.type === "text" && (isHtml(data!.content) || isMarkdown(data!.content) || isJson(data!.content) || isUrl(data!.content))) ||
    data!.type === "url",
  [data]);

  return (
    <DraggableWidgetCard
      widgetId={widget.id}
      dataId={data!.id}
      globalEntries={globalSelection.entries}
    >
      <ItemContextMenu
        area="widget"
        itemId={widget.id}
        dataId={data!.id}
        onRightClickSelect={handleRightClickSelect}
        onDelete={() => setShowDeleteDialog(true)}
      >
        <motion.div
          layout="position"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`glass-bar rounded-xl border-2 p-3 flex flex-col gap-2 group relative cursor-pointer transition-all ${
            selectedWidgetIds.has(widget.id)
              ? "border-primary bg-primary/10 shadow-[0_0_8px_hsl(var(--primary)/0.3)]"
              : "border-border/40 hover:border-primary/40"
          }`}
          data-selectable-id={widget.id}
          data-selectable-data-id={data!.id}
          data-selectable-area="widget"
          onClick={(e) => handleCardClick(widget.id, data!.id, e)}
        >
          <div
            className="overflow-hidden rounded-lg bg-background/30 p-2 relative"
            style={{ height: `${widgetHeight}px` }}
          >
            {data!.type === "image" ? (
              <img
                src={data!.content}
                alt={data!.label}
                className="w-full h-full object-contain rounded"
              />
            ) : showRaw ? (
              <pre className="text-[10px] text-muted-foreground/80 font-mono whitespace-pre-wrap break-all leading-tight">
                {data!.content}
              </pre>
            ) : (
              <RichContent content={data!.content} />
            )}

            {/* 원문 보기 버튼 */}
            {isRich && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRaw(!showRaw);
                }}
                className={`absolute bottom-1 right-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-medium transition-colors backdrop-blur-sm shadow-sm ${
                  showRaw
                    ? 'bg-primary/20 text-primary border border-primary/40'
                    : 'bg-background/60 text-muted-foreground border border-border/50 hover:text-foreground hover:bg-background/80'
                }`}
              >
                {showRaw ? <EyeOff className="w-2.5 h-2.5" /> : <Eye className="w-2.5 h-2.5" />}
                {showRaw ? t('preview.rendered') : t('preview.raw_source')}
              </button>
            )}
          </div>

          {editingLabel?.dataId === data!.id ? (
            <span className="text-xs text-muted-foreground truncate" onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={editingLabel.value}
                onChange={(e) => setEditingLabel({ ...editingLabel, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleLabelSave();
                  if (e.key === "Escape") setEditingLabel(null);
                }}
                className="w-full text-xs px-1 py-0.5 rounded bg-background/60 border border-border/50 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </span>
          ) : (
            <span className="text-xs text-muted-foreground truncate">
              {data!.label}
            </span>
          )}

          <div
            className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            {editingLabel?.dataId === data!.id ? (
              <>
                <button
                  onClick={handleLabelSave}
                  className="p-1 rounded-md bg-background/60 hover:bg-primary/20 text-primary hover:text-primary/80 transition-colors"
                  title={t("menu.save")}
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setEditingLabel(null)}
                  className="p-1 rounded-md bg-background/60 hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors"
                  title={t("menu.cancel")}
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditingLabel({ dataId: data!.id, value: data!.label })}
                  className="p-1 rounded-md bg-background/60 hover:bg-accent/20 text-muted-foreground hover:text-accent-foreground transition-colors"
                  title={t("menu.edit_label")}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={() => {
                    globalSelection.clearAll();
                    globalSelection.addEntries([{ area: 'widget', itemId: widget.id, dataId: data!.id }]);
                    setShowDeleteDialog(true);
                  }}
                  className="p-1 rounded-md bg-background/60 hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                  title={t("menu.delete")}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        </motion.div>
      </ItemContextMenu>
    </DraggableWidgetCard>
  );
}
